import { InputHTMLAttributes, ReactElement, ReactNode } from 'react'

import { iconIdMapping } from '@arcade/web/icons/react'
import {
  BaseCell,
  BaseCellProps,
  CheckboxCell,
  InteractiveCell,
  PushCell,
  RadioCell,
  Text,
} from '@cash-design-system/react'
import { ArcadeAvatarFromUiAvatar } from '@cash-web/avatar-from-proto'
import type {
  CellDefault,
  CellDefaultCellDefaultIcon,
} from '@cash-web/protos/squareup/cash/ui/arcade/elements/CellDefault.pb'
import logger from '@cash-web/shared/util-logger'

import { ArcadeAccessoryLabeledPushFromProto } from '../ArcadeAccessoryLabeledPushFromProto/ArcadeAccessoryLabeledPushFromProto'
import { ArcadeCellLabelAccessoryWrapper } from '../ArcadeCellLabelAccessoryWrapper/ArcadeCellLabelAccessoryWrapper'
import { TintedArcadeCellIcon } from '../TintedArcadeCellIcon/TintedArcadeCellIcon'

type RenderElement = (element: ReactElement) => ReactElement

const RenderedLabel = ({
  renderLabel,
  className,
  children,
}: {
  renderLabel: RenderElement
  className?: string
  children?: ReactNode
}) => renderLabel(<span className={className}>{children}</span>)

const getAssetIcon = ({
  protoProps,
  renderAvatar = avatar => avatar,
}: {
  protoProps: CellDefaultCellDefaultIcon
  renderAvatar?: RenderElement
}): BaseCellProps['asset'] | undefined => {
  if (protoProps?.tinted_icon) {
    return { avatar: renderAvatar(<TintedArcadeCellIcon tintedIcon={protoProps.tinted_icon} />) }
  } else if (protoProps?.avatar) {
    return { avatar: renderAvatar(<ArcadeAvatarFromUiAvatar uiAvatar={protoProps.avatar} size="small" />) }
  } else if (protoProps?.icon) {
    const iconArcadeId = protoProps.icon.arcade_id
    const iconInfo = iconArcadeId ? iconIdMapping[iconArcadeId] : undefined
    if (!iconInfo) {
      // If you run into this error it either means we need to update our mapping, or the server
      // accidentally provided an invalid arcade id.
      logger.warn(new Error(`ArcadeCellIconFromProto: iconArcadeId is missing from mapping: ${iconArcadeId}`))
      return undefined
    }
    return { Icon: iconInfo.Icon }
  }

  logger.warn(new Error('ArcadeCellIconFromProto: no icon, tinted_icon, or avatar found in protoProps'))
  return undefined
}

export function ArcadeCellFromProto({
  protoProps,
  onClick,
  selectionGroupId,
  initialSelection,
  accessoryInputProps,
  accessoryAlignment,
  assetAlignment,
  collapseHiddenChevronSpace = false,
  suppressPushBehavior = false,
  renderAvatar,
  renderBody = body => body,
  renderAccessory = accessory => accessory,
  renderLabel,
}: {
  protoProps: CellDefault
  onClick?: () => void
  selectionGroupId?: string
  initialSelection?: boolean
  accessoryInputProps?: InputHTMLAttributes<HTMLInputElement>
  accessoryAlignment?: BaseCellProps['accessoryAlignment']
  assetAlignment?: BaseCellProps['assetAlignment']
  collapseHiddenChevronSpace?: boolean
  // For containers that already own tap handling: push accessories render static and their onClick is ignored.
  suppressPushBehavior?: boolean

  renderAvatar?: RenderElement
  renderLabel?: RenderElement
  renderBody?: RenderElement
  renderAccessory?: RenderElement
}) {
  // TODO: Implement
  // 4	CellDefaultAccessory	accessory

  const label = protoProps.label_text?.translated_value ?? ''
  const bodyText = protoProps.body_text?.translated_value
  const body2Text = protoProps.body_2_text?.translated_value
  const body = bodyText && (
    <>
      {bodyText}
      {body2Text && <br />}
      {body2Text}
    </>
  )
  const staticBody = body && renderBody(body)
  const labelProps = renderLabel ? ({ as: RenderedLabel, renderLabel } as BaseCellProps['labelProps']) : undefined

  const disabled = protoProps.disabled
  const icon = protoProps.icon
  const accessory = protoProps.accessory
  const accessoryLeadingImage = protoProps.accessory_leading_image

  const labelIcon = protoProps.label_icon ? (
    <TintedArcadeCellIcon
      tintedIcon={{
        icon: protoProps.label_icon,
        icon_tint_color: protoProps.label_icon.tint_color,
      }}
      isBackgroundRequired={false}
    />
  ) : undefined

  const asset = icon ? getAssetIcon({ protoProps: icon, renderAvatar }) : undefined

  if (protoProps.body_icon) {
    // If you run into this error you'll need to add support for the body_icon. It wasn't originally
    // supported for time reasons -- the first use case of this component didn't need it.
    logger.warn(new Error('ArcadeCellFromProto: body_icon is not yet supported'))
  }

  if (protoProps.is_badged) {
    // If you run into this error you'll need to add support for is_badged. It wasn't originally
    // supported for time reasons -- the first use case of this component didn't need it.
    logger.warn(new Error('ArcadeCellFromProto: is_badged is not yet supported'))
  }

  if (accessory?.labeled_push) {
    const labeledPushAccessory = (
      <ArcadeAccessoryLabeledPushFromProto
        accessoryLeadingImage={accessoryLeadingImage}
        collapseHiddenChevronSpace={collapseHiddenChevronSpace}
        protoProps={accessory.labeled_push}
        disabled={disabled}
      />
    )

    if (suppressPushBehavior) {
      return (
        <BaseCell
          asset={asset}
          assetAlignment={assetAlignment}
          label={label}
          labelProps={labelProps}
          body={staticBody}
          disabled={disabled}
          accessory={renderAccessory(labeledPushAccessory)}
          accessoryAlignment={accessoryAlignment ?? 'center'}
        />
      )
    }

    return (
      <InteractiveCell
        label={label}
        disabled={disabled}
        cellProps={{
          asset,
          assetAlignment,
          body,
          accessory: labeledPushAccessory,
        }}
        onClick={onClick}
      />
    )
  } else if (accessory?.push) {
    if (suppressPushBehavior) {
      return (
        <BaseCell
          asset={asset}
          assetAlignment={assetAlignment}
          label={label}
          labelProps={labelProps}
          body={staticBody}
          disabled={disabled}
        />
      )
    }

    return <PushCell label={label} disabled={disabled} cellProps={{ asset, assetAlignment, body }} onClick={onClick} />
  } else if (accessory?.label) {
    return (
      <BaseCell
        asset={asset}
        assetAlignment={assetAlignment}
        label={label}
        labelProps={labelProps}
        body={staticBody}
        disabled={disabled}
        accessory={renderAccessory(
          <ArcadeCellLabelAccessoryWrapper accessoryLeadingImage={accessoryLeadingImage}>
            <Text typography="bodyMedium" textWrap="nowrap">
              {accessory.label?.translated_value}
            </Text>
          </ArcadeCellLabelAccessoryWrapper>
        )}
        accessoryAlignment={accessoryAlignment ?? 'center'}
      />
    )
  } else if (accessory?.radio) {
    return (
      <RadioCell
        label={label}
        disabled={disabled}
        cellProps={{
          asset,
          assetAlignment,
          body,
          labelIcon,
          ...(labelIcon && { labelProps: { labelOverflow: 'ellipsis' } }),
        }}
        defaultChecked={initialSelection}
        onClick={onClick}
        {...accessoryInputProps}
      />
    )
  } else if (accessory?.checkbox) {
    return (
      <CheckboxCell
        label={label}
        disabled={disabled}
        cellProps={{
          asset,
          assetAlignment,
          body,
          labelIcon,
          ...(labelIcon && { labelProps: { labelOverflow: 'ellipsis' } }),
        }}
        onClick={onClick}
        defaultChecked={initialSelection}
        {...accessoryInputProps}
      />
    )
  } else if (accessory) {
    // If you run into this error you'll need to add support for the missing accessory. It wasn't originally
    // supported for time reasons -- the first use case of this component didn't need it.
    logger.warn(
      new Error(
        'ArcadeCellFromProto: only labeled-push, push, radio, checkbox, or label accessories are currently supported'
      )
    )

    // Since we don't support any other accessory types yet, just return a base cell.
    return (
      <BaseCell
        asset={asset}
        assetAlignment={assetAlignment}
        label={label}
        labelProps={labelProps}
        body={staticBody}
        disabled={disabled}
      />
    )
  } else {
    return (
      <BaseCell
        asset={asset}
        assetAlignment={assetAlignment}
        label={label}
        labelProps={labelProps}
        body={staticBody}
        disabled={disabled}
      />
    )
  }
}

export default ArcadeCellFromProto
